import { ConnStateInfo, KCSDK } from "@/sdk"
import { LogData } from "@/utils/data_report"
import { DataReportUsecase } from "./data_report"

let connStatsTimer: number | null = null

// 已经收集到的要上报的统计信息（video 与 output_video 混合累计）
let gatheredStatsList: LogData[] = []
// 批量上报的数量，对齐 android 端 _batchCount=60
const batchNum = 60

/**
 *  开始收集云桌面视频流状态信息
 */
function starGatheringData(callback: (connState: ConnStateInfo) => void): void {
  if (connStatsTimer !== null) {
    window.clearInterval(connStatsTimer)
  }
  gatheredStatsList = []
  const poolTask = async () => {
    const state = await KCSDK.getConnStats()

    if (state != null) {
      callback(state)
      // 上报数据
      reportData(state)
    }
  }
  poolTask()
  connStatsTimer = window.setInterval(() => {
    poolTask()
  }, 1000)
}

/**
 * 停止收集云桌面视频流状态信息
 */
async function stopGatheringData(): Promise<void> {
  if (connStatsTimer === null) {
    return
  }
  // 上报剩余数据
  DataReportUsecase.reportLogs(gatheredStatsList)
  gatheredStatsList = []

  window.clearInterval(connStatsTimer)
  connStatsTimer = null
}

function reportData(data: ConnStateInfo) {
  const { inputVideoData, otherData, outputVideoData } = data

  const log = new LogData("video", {
    screen: "main",
    rtt: otherData.rtt, // 网络往返延迟
    cur_total_bytes_recv: otherData.transportCurBytesReceived, // 码率 transport中的，是总的
    receive_bps: otherData.transportCurBytesReceived * 8,

    fps: inputVideoData.fps ?? "-",
    cur_bytes_recv: inputVideoData.curBytesReceived,
    cur_frame_recv: inputVideoData.curFrameRecv,
    cur_frame_decoded: inputVideoData.curFramesDecoded,
    lost: inputVideoData.lostRate,
    packet_loss_rate:
      inputVideoData.lostRate == null ? null : inputVideoData.lostRate * 100,
    codec: inputVideoData.codec,
    decoder: inputVideoData.decoder,
    freeze_count: inputVideoData.freezeCount,
    total_freezes_duration: inputVideoData.totalFreezesDuration,
    frames_received: inputVideoData.framesReceived,
    frames_decoded: inputVideoData.framesDecoded,
    packets_received: inputVideoData.packetsReceived,
    frames_dropped: inputVideoData.framesDropped,
    key_frames_decoded: inputVideoData.keyFramesDecoded,
    resolution:
      inputVideoData.frameWidth != null && inputVideoData.frameHeight != null
        ? `${inputVideoData.frameWidth}x${inputVideoData.frameHeight}`
        : "-",
    log_timestamp: Date.now(),
  })
  gatheredStatsList.push(log)

  // output_video 是摄像头输出流（outbound-rtp）。摄像头关闭后 Electron/WebRTC
  // 仍可能保留 outbound-rtp video 报告（mid、累计 framesEncoded 等残留），
  // 但停止编码发送——当前字节率(bytesSent 增量)与帧率(fps)均为 0。
  // 因此以"是否有实际发送活动"为判据：两者都为 0 时视为摄像头未发送，
  // 跳过上报，对齐 android 端 track 移除后 sendVideos 不含该流的行为。
  const hasOutboundActivity =
    (outputVideoData.bytesSent ?? 0) > 0 || (outputVideoData.fps ?? 0) > 0
  if (hasOutboundActivity) {
    const outputLog = new LogData("output_video", {
      id: outputVideoData.mid, // WebRTC 中 outboundVideoRTP里的 mid (mediaSourceId), 标记是哪路视频输出流
      width: outputVideoData.frameWidth,
      height: outputVideoData.frameHeight,
      fps: outputVideoData.fps,
      jitter: outputVideoData.jitter,
      encoder: outputVideoData.encoder,
      total_encode_time: outputVideoData.totalEncodeTime,
      bytes_per_second: outputVideoData.bytesSent,
      send_bps: outputVideoData.bytesSent * 8,
      frames_encoded: outputVideoData.framesEncoded,
      lost_rate: outputVideoData.lostRate,
      packet_loss_rate:
        outputVideoData.lostRate == null ? null : outputVideoData.lostRate * 100,
      log_timestamp: Date.now(),
    })
    gatheredStatsList.push(outputLog)
  }

  if (gatheredStatsList.length < batchNum) {
    return
  }

  // 上报数据
  DataReportUsecase.reportLogs(gatheredStatsList)
  gatheredStatsList = []
}

function onDisconnect() {
  gatheredStatsList = []
}

function writeStatsInfoToCSVFile() {
  KCSDK.writeStatsInfoToCSVFile()
}

export const VideoStatsUseCase = {
  starGatheringData,
  stopGatheringData,
  onDisconnect,
  writeStatsInfoToCSVFile,
}
